const justifyContent = {
    name: "justify-content",
    path: "/justify-content",
    title: "justify-content",
    description: "Utilities for controlling how flex and grid items are positioned along a container's main axis.",
    value: {
        "justify-content": {
            "justify-start": "flex-start",
            "justify-end": "flex-end",
            "justify-end-safe": "safe flex-end",
            "justify-center": "center",
            "justify-center-safe": "safe center",
            "justify-between": "space-between",
            "justify-around": "space-around",
            "justify-evenly": "space-evenly",
            "justify-stretch": "stretch",
            "justify-normal": "normal",
            "justify-baseline": "baseline",
        },
    },
    exampleData: [
        {
            "example": {
                "code": `
<div class="flex justify-start ...">
  <div>01</div>
  <div>02</div>
  <div>03</div>
</div>
                `
            },
        },
        {
            "example": {
                "code": `
<div class="flex justify-center ...">
  <div>01</div>
  <div>02</div>
  <div>03</div>
  <div>04</div>
</div>

<div class="flex justify-center-safe ...">
  <div>01</div>
  <div>02</div>
  <div>03</div>
  <div>04</div>
</div>
`
            },
        },
        {
            "example": {
                "code": `
<div class="flex justify-end ...">
  <div>01</div>
  <div>02</div>
  <div>03</div>
  <div>03</div>
</div>

<div class="flex justify-end-safe ...">
  <div>01</div>
  <div>02</div>
  <div>03</div>
  <div>03</div>
</div>
`
            },
        },
        {
            "example": {
                "code": `
<div class="flex justify-between ...">
  <div>01</div>
  <div>02</div>
  <div>03</div>
</div>


<div class="flex justify-around ...">
  <div>01</div>
  <div>02</div>
  <div>03</div>
</div>
`
            },
        },
        {
            "example": {
                "code": `
<div class="flex justify-evenly ...">
  <div>01</div>
  <div>02</div>
  <div>03</div>
</div>


<div class="grid grid-flow-col justify-stretch ...">
  <div>01</div>
  <div>02</div>
  <div>03</div>
</div>


`
            },
        }
    ],
    htmlData: [
        {
            data: `
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
                Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">justify-start</code>
                  utility to justify items against the start of the container's main axis:
            </p>

            <div class="relative">
                <div class="absolute inset-0 z-0">
                    <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                </div>
                <div class="flex justify-start gap-4 font-mono text-sm leading-6 font-bold text-color-white relative">
                    <div class="flex size-14 shrink-0 items-center justify-center rounded-lg bg-color-pink-500 p-4">01</div>
                    <div class="flex size-14 shrink-0 items-center justify-center rounded-lg bg-color-pink-500 p-4">02</div>
                    <div class="flex size-14 shrink-0 items-center justify-center rounded-lg bg-color-pink-500 p-4">03</div>              </div>
            </div>

            
            `
        },
        {
            data: `
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
                Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">justify-center, justify-center-safe</code>
                  utilities to justify items along the center of the container's main axis:
            </p>
            <small class="text-center block mb-4">justify-center</small>
            <div class="relative">
                <div class="absolute inset-0 z-0">
                    <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                </div>
                
                <div class="flex justify-center gap-4 font-mono text-sm leading-6 font-bold text-color-white relative">
                    <div class="flex size-14 shrink-0 items-center justify-center rounded-lg bg-color-pink-500 p-4">01</div>
                    <div class="flex size-14 shrink-0 items-center justify-center rounded-lg bg-color-pink-500 p-4">02</div>
                    <div class="flex size-14 shrink-0 items-center justify-center rounded-lg bg-color-pink-500 p-4">03</div>              </div>
            </div>

            <small class="text-center block mb-4 mt-8">justify-center-safe</small>
            <div class="relative">
                <div class="absolute inset-0 z-0">
                    <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                </div>
                
                <div class="flex justify-center-safe gap-4 font-mono text-sm leading-6 font-bold text-color-white relative">
                    <div class="flex size-14 shrink-0 items-center justify-center rounded-lg bg-color-pink-500 p-4">01</div>
                    <div class="flex size-14 shrink-0 items-center justify-center rounded-lg bg-color-pink-500 p-4">02</div>
                    <div class="flex size-14 shrink-0 items-center justify-center rounded-lg bg-color-pink-500 p-4">03</div>              </div>
            </div>

            
            `
        },
        {
            data: `
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
                Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">justify-end, justify-end-safe</code>
                  tilities to justify items against the end of the container's main axis:
            </p>
            <small class="text-center block mb-4">justify-end</small>
            <div class="relative">
                <div class="absolute inset-0 z-0">
                    <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                </div>
                
                <div class="flex justify-end gap-4 font-mono text-sm leading-6 font-bold text-color-white relative">
                    <div class="flex size-14 shrink-0 items-center justify-center rounded-lg bg-color-pink-500 p-4">01</div>
                    <div class="flex size-14 shrink-0 items-center justify-center rounded-lg bg-color-pink-500 p-4">02</div>
                    <div class="flex size-14 shrink-0 items-center justify-center rounded-lg bg-color-pink-500 p-4">03</div>              </div>
            </div>

            <small class="text-center block mb-4 mt-8">justify-end-safe</small>
            <div class="relative">
                <div class="absolute inset-0 z-0">
                    <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                </div>
                
                <div class="flex justify-end-safe gap-4 font-mono text-sm leading-6 font-bold text-color-white relative">
                    <div class="flex size-14 shrink-0 items-center justify-center rounded-lg bg-color-pink-500 p-4">01</div>
                    <div class="flex size-14 shrink-0 items-center justify-center rounded-lg bg-color-pink-500 p-4">02</div>
                    <div class="flex size-14 shrink-0 items-center justify-center rounded-lg bg-color-pink-500 p-4">03</div>              </div>
            </div>

            
            `
        },
        {
            data: `
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
                Use the  <code class="text-color-gray-800 dark:text-color-white font-semibold">justify-between</code>
                  justify-between utility to justify items along the container's main axis such that there is an equal amount of space between each item:
            </p>
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
                Use the  <code class="text-color-gray-800 dark:text-color-white font-semibold">justify-around</code>
                  utility to justify items along the container's main axis such that there is an equal amount of space on each side of each item:
            </p>
            <small class="text-center block mb-4">justify-between</small>
            <div class="relative">
                <div class="absolute inset-0 z-0">
                    <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                </div>
                
                <div class="flex justify-between gap-4 font-mono text-sm leading-6 font-bold text-color-white relative">
                    <div class="flex size-14 shrink-0 items-center justify-center rounded-lg bg-color-pink-500 p-4">01</div>
                    <div class="flex size-14 shrink-0 items-center justify-center rounded-lg bg-color-pink-500 p-4">02</div>
                    <div class="flex size-14 shrink-0 items-center justify-center rounded-lg bg-color-pink-500 p-4">03</div>              </div>
            </div>

            <small class="text-center block mb-4 mt-8">justify-around</small>
            <div class="relative">
                <div class="absolute inset-0 z-0">
                    <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                </div>
                
                <div class="flex justify-around gap-4 font-mono text-sm leading-6 font-bold text-color-white relative">
                    <div class="flex size-14 shrink-0 items-center justify-center rounded-lg bg-color-pink-500 p-4">01</div>
                    <div class="flex size-14 shrink-0 items-center justify-center rounded-lg bg-color-pink-500 p-4">02</div>
                    <div class="flex size-14 shrink-0 items-center justify-center rounded-lg bg-color-pink-500 p-4">03</div>              </div>
            </div>

            
            `
        },
        {
            data: `
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
                Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">justify-evenly</code>
                 utility to justify items along the container's main axis such that there is an equal amount of space around each item, but also accounting for the doubling of space you would normally see between each item when using justify-around:
            </p>
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
                Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">justify-stretch</code>
                 utility to allow content items to fill the available space along the container's main axis:
            </p>
            <small class="text-center block mb-4">justify-evenly</small>
            <div class="relative">
                <div class="absolute inset-0 z-0">
                    <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                </div>
                
                <div class="flex justify-evenly gap-4 font-mono text-sm leading-6 font-bold text-color-white relative">
                    <div class="flex size-14 shrink-0 items-center justify-center rounded-lg bg-color-pink-500 p-4">01</div>
                    <div class="flex size-14 shrink-0 items-center justify-center rounded-lg bg-color-pink-500 p-4">02</div>
                    <div class="flex size-14 shrink-0 items-center justify-center rounded-lg bg-color-pink-500 p-4">03</div>              </div>
            </div>

            <small class="text-center block mb-4 mt-8">justify-stretch</small>
            <div class="relative">
                <div class="absolute inset-0 z-0">
                    <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                </div>
                
                <div class="grid grid-flow-col justify-stretch gap-4 font-mono text-sm leading-6 font-bold text-color-white relative">
                    <div class="flex h-14 items-center justify-center rounded-lg bg-color-pink-500 p-4">01</div>
                    <div class="flex h-14 items-center justify-center rounded-lg bg-color-pink-500 p-4">02</div>
                    <div class="flex h-14 items-center justify-center rounded-lg bg-color-pink-500 p-4">03</div>              </div>
            </div>

            
            `
        },
    ]
};



export default justifyContent;

<div className="sr-only justify-start justify-center-safe justify-end justify-end-safe justify-around justify-between justify-stretch justify-evenly">



</div>